import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const PlantManagement = ({ company, onAddPlant, onUpdatePlant, onDeletePlant }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    facilityType: '',
    capacity: '',
    status: 'Active'
  });

  const facilityTypeOptions = [
    { value: 'Production', label: 'Production' },
    { value: 'Assembly', label: 'Assembly' },
    { value: 'Warehouse', label: 'Warehouse' },
    { value: 'Distribution', label: 'Distribution' },
    { value: 'Research', label: 'Research' }
  ];

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
    { value: 'Maintenance', label: 'Maintenance' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (editingPlant) {
      onUpdatePlant(editingPlant?.id, formData);
      setEditingPlant(null);
    } else {
      onAddPlant(formData);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      facilityType: '',
      capacity: '',
      status: 'Active'
    });
    setIsAdding(false);
    setEditingPlant(null);
  };

  const handleEdit = (plant) => {
    setEditingPlant(plant);
    setFormData({
      name: plant?.name,
      location: plant?.location,
      facilityType: plant?.facilityType,
      capacity: plant?.capacity,
      status: plant?.status
    });
    setIsAdding(true);
  };

  if (!company) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="text-center py-8">
          <Icon name="Factory" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Plant Management</h3>
          <p className="text-muted-foreground text-sm">Select a company to manage its plants</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Plants</h3>
          <p className="text-sm text-muted-foreground">{company?.name}</p>
        </div>
        {!isAdding && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => setIsAdding(true)}
          >
            Add Plant
          </Button>
        )}
      </div>
      {isAdding && (
        <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 bg-muted/30 rounded-lg">
          <Input
            label="Plant Name"
            type="text"
            placeholder="Enter plant name"
            required
            value={formData?.name}
            onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
          />

          <Input
            label="Location"
            type="text"
            placeholder="City, State/Country"
            required
            value={formData?.location}
            onChange={(e) => setFormData({ ...formData, location: e?.target?.value })}
          />

          <Select
            label="Facility Type"
            options={facilityTypeOptions}
            value={formData?.facilityType}
            onChange={(value) => setFormData({ ...formData, facilityType: value })}
            placeholder="Select type"
            required
          />

          <Input
            label="Capacity"
            type="text"
            placeholder="e.g., 10,000 units/month"
            value={formData?.capacity}
            onChange={(e) => setFormData({ ...formData, capacity: e?.target?.value })}
          />

          <Select
            label="Status"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => setFormData({ ...formData, status: value })}
          />

          <div className="flex gap-2">
            <Button type="submit" variant="default" size="sm" fullWidth>
              {editingPlant ? 'Update' : 'Add'}
            </Button>
            <Button type="button" variant="outline" size="sm" fullWidth onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      <div className="space-y-3">
        {company?.plants?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Factory" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No plants added yet</p>
          </div>
        ) : (
          company?.plants?.map((plant) => (
            <div
              key={plant?.id}
              className="p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="Factory" size={16} className="text-primary" />
                  <h4 className="font-medium text-foreground">{plant?.name}</h4>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="Edit"
                    onClick={() => handleEdit(plant)}
                  />
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="Trash2"
                    onClick={() => onDeletePlant(plant?.id)}
                  />
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="MapPin" size={14} />
                  <span>{plant?.location}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Building" size={14} />
                  <span>{plant?.facilityType}</span>
                </div>
                {plant?.capacity && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Gauge" size={14} />
                    <span>{plant?.capacity}</span>
                  </div>
                )}
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      plant?.status === 'Active' ?'bg-success/10 text-success'
                        : plant?.status === 'Maintenance' ?'bg-warning/10 text-warning' :'bg-muted text-muted-foreground'
                    }`}
                  >
                    {plant?.status}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PlantManagement;